import React from 'react';
import './About.css';

function About() {
  return (
    <div className="about-container">
      <div className="about-content">
        <h2>About Me</h2>

        <div className="about-section">
          <p className="intro">
            Welcome to my AFL prediction site! I'm the founder and lead data scientist, combining my passion for
            Australian football with modern machine learning techniques to provide accurate weekly predictions for the AFL season.
          </p>
        </div>

        <div className="about-section">
          <h3>My Approach</h3>
          <p>
            With years of experience in data science and a deep understanding of AFL, I've created a platform where
            data drives insights. By using advanced machine learning methods, I analyze a range of factors—from player
            form and injuries to team dynamics and historical trends—to deliver precise predictions each week.
          </p>
        </div>

        <div className="about-section">
          <h3>What I Offer</h3>
          <p>
            I focus on providing more than just numbers; my goal is to offer actionable insights that help you make
            informed decisions. Whether you're a fan, a bettor, or simply an AFL enthusiast, my predictions are designed
            to take the guesswork out of the game.
          </p>
        </div>

        <div className="about-section">
          <h3>Weekly Analysis</h3>
          <p>
            Every week, I dive into the latest data to predict match outcomes, player performances, and trends, ensuring
            that you have access to the most up-to-date insights. My approach blends rigorous data analysis with a deep
            understanding of the sport, making sure my predictions are grounded in both data and the nuances of AFL.
          </p>
        </div>

        <div className="about-section closing">
          <p>
            Thank you for visiting, and I look forward to providing you with valuable insights throughout the AFL season!
          </p>
        </div>

        <div className="about-section">
          <h3>The Model</h3>
          <p>
            Footy Science runs two prediction models side by side.
          </p>
          <h4>Logistic Model</h4>
          <p>
            The original model is a custom logistic regression trained on rolling player and team statistics
            from 2012 to present. Features are quantile-binned into five levels and the model is trained
            with L1 regularisation and isotonic calibration, so predicted probabilities closely match
            observed win rates. It produces a single win probability per match.
          </p>
          <h4>Phase Model</h4>
          <p>
            The Phase Model is a phase-structured neural network that decomposes each match into five
            game phases — Accumulation, Territory, Chance Creation, Conversion, and Momentum. A small
            sub-network processes the features relevant to each phase independently, producing a phase
            score for each team. These scores feed into a margin head and an uncertainty head, giving:
          </p>
          <ul>
            <li><strong>Win probability</strong> — derived from the predicted margin and uncertainty via a normal CDF</li>
            <li><strong>Predicted margin</strong> — the expected point difference (positive = home team wins)</li>
            <li><strong>Uncertainty (±)</strong> — one standard deviation around the margin estimate; wider = less certain</li>
            <li><strong>Phase scores</strong> — which team has the advantage in each game phase</li>
            <li><strong>Player influence</strong> — which players drive each phase score</li>
          </ul>
          <p>
            Both models are retrained after each round using the latest scraped data from FootyWire.
            Test set accuracy (held-out matches) is approximately 68% for both models.
          </p>
        </div>

        <div className="about-section">
          <h3>Glossary</h3>

          <h4>Prediction</h4>
          <dl className="glossary">
            <dt>Win probability</dt>
            <dd>The model's estimated chance that the home team wins, expressed as a percentage. 50% = coin flip.</dd>
            <dt>Confidence</dt>
            <dd>How far the win probability is from 50%. High ≥ 70%, Medium ≥ 60%, Low &lt; 60%.</dd>
            <dt>Predicted margin</dt>
            <dd>The Phase Model's best estimate of the final point difference. Positive means the home team wins by that margin.</dd>
            <dt>Uncertainty (±)</dt>
            <dd>One standard deviation around the margin estimate. A margin of +10 ± 35 means the model expects a close, hard-to-predict game; +40 ± 20 means a comfortable win is likely.</dd>
          </dl>

          <h4>Phase Breakdown</h4>
          <dl className="glossary">
            <dt>Accumulation</dt>
            <dd>Ball-winning and ball-use — disposals, disposal efficiency, contested possessions, clearances, turnovers. Teams that win this phase control possession.</dd>
            <dt>Territory</dt>
            <dd>Field position — metres gained, inside 50s, rebound 50s. Teams that win this phase spend more time in attacking positions.</dd>
            <dt>Chance Creation</dt>
            <dd>Creating scoring opportunities — score involvements and scoring shots. Teams that win this phase generate more shots at goal.</dd>
            <dt>Conversion</dt>
            <dd>Turning chances into points — shot margin (goals vs behinds ratio), average winning margin, fantasy scoring. Teams that win this phase are more clinical.</dd>
            <dt>Momentum</dt>
            <dd>Context and form — recent wins, squad experience, travel disadvantage. This phase captures structural advantages that persist across games.</dd>
            <dt>Phase score</dt>
            <dd>A unitless number produced by the neural network sub-network for that phase. Positive means the home team has the advantage; negative means the away team does. The bar length shows how large the advantage is relative to the other phases in the same match.</dd>
          </dl>

          <h4>Player Influence</h4>
          <dl className="glossary">
            <dt>Influence share</dt>
            <dd>How much each player's rolling statistics contribute to their team's phase scores, weighted by how sensitive the model is to those statistics at the current feature values. It is a relative measure within the team — the longest bar is the most influential player, not an absolute rating. Momentum is excluded because it is a team-level stat that cannot be attributed to individuals.</dd>
          </dl>

        </div>

        <div className="about-section about-support">
          <h3>Support the Project</h3>
          <p>
            Footy Science is a free, independent project. If you find it useful, a coffee goes a long way towards keeping the servers running and the model improving.
          </p>
          <a
            className="coffee-btn"
            href="https://buymeacoffee.com/footy_science"
            target="_blank"
            rel="noopener noreferrer"
          >
            ☕ Buy me a coffee
          </a>
        </div>
      </div>
    </div>
  );
}

export default About;
